import { Button } from "@progress/kendo-react-buttons";
import { Droppable, SvgIcon } from "@progress/kendo-react-common";
import { plusIcon, xIcon } from "@progress/kendo-svg-icons";
import { useState } from "react";
import type { ScenarioLibraryScenario } from "./scenarioLibrary";

type ScenarioComparisonProps = {
    selectedScenarios: (ScenarioLibraryScenario | null)[];
    draggingScenario: ScenarioLibraryScenario | null;
    onScenarioDrop: (slotIndex: number) => void;
    onScenarioRemove: (slotIndex: number) => void;
};

type ScenarioComparisonSlotProps = {
    scenario: ScenarioLibraryScenario | null;
    slotIndex: number;
    draggingScenario: ScenarioLibraryScenario | null;
    onScenarioDrop: (slotIndex: number) => void;
    onScenarioRemove: (slotIndex: number) => void;
};

const ScenarioComparisonSlot: React.FC<ScenarioComparisonSlotProps> = ({ scenario, slotIndex, draggingScenario, onScenarioDrop, onScenarioRemove }) => {
    const [isHovering, setIsHovering] = useState<boolean>(false);
    const isValidDropTarget: boolean = draggingScenario !== null && draggingScenario.id !== scenario?.id;
    const isDropActive: boolean = isValidDropTarget && isHovering;
    const emptySlotStyles = isDropActive
        ? {
                backgroundColor: "#dbeafe",
                borderColor: "#0d6efd",
                color: "#084298"
            }
        : isValidDropTarget
            ? {
                    backgroundColor: "#ecfdf5",
                    borderColor: "#86efac",
                    color: "#047857"
                }
            : {
                    backgroundColor: "#ffffff",
                    borderColor: "#cbd5e1",
                    color: "#64748b"
                };
    const occupiedSlotStyles = isDropActive
        ? {
                backgroundColor: "#eff6ff",
                borderColor: "#0d6efd"
            }
        : isValidDropTarget
            ? {
                    backgroundColor: "#f0fdf4",
                    borderColor: "#22c55e"
                }
            : {
                    backgroundColor: "#ffffff",
                    borderColor: "#dce4ef"
                };

    return (
        <Droppable
            onDragEnter={() => setIsHovering(true)}
            onDragLeave={() => setIsHovering(false)}
            onDrop={() => {
                if (isValidDropTarget) {
                    onScenarioDrop(slotIndex);
                }
                setIsHovering(false);
            }}
        >
            <div
                className="d-flex flex-column rounded-4 flex-fill h-100"
                style={{
                    minWidth: "220px",
                    minHeight: "360px",
                    border: `2px dashed ${scenario ? occupiedSlotStyles.borderColor : emptySlotStyles.borderColor}`,
                    backgroundColor: scenario ? occupiedSlotStyles.backgroundColor : emptySlotStyles.backgroundColor,
                    color: emptySlotStyles.color,
                    transition: "background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease",
                    boxShadow: isDropActive ? "0 0 0 0.25rem rgba(13, 110, 253, 0.18)" : "none"
                }}
            >
                {scenario
                    ? (
                            <div className="d-flex flex-column h-100 p-3 text-start">
                                <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
                                    <div>
                                        <p className="small text-secondary mb-1">
                                            Scenario
                                            {" "}
                                            {slotIndex + 1}
                                        </p>
                                        <h2 className="h6 fw-bold text-body mb-0">{scenario.title}</h2>
                                    </div>
                                    <Button
                                        type="button"
                                        fillMode="flat"
                                        size="small"
                                        svgIcon={xIcon}
                                        aria-label={`Remove ${scenario.title}`}
                                        onClick={() => onScenarioRemove(slotIndex)}
                                    />
                                </div>

                                <div className="d-flex align-items-center gap-2 small text-secondary mb-3">
                                    <span>{scenario.retailer}</span>
                                    <span aria-hidden="true">•</span>
                                    <span>{scenario.date}</span>
                                </div>

                                <p className="small text-body-secondary mb-3" style={{ lineHeight: 1.45 }}>{scenario.description}</p>

                                <div className="row g-2 mb-3">
                                    {scenario.metrics.map(metric => (
                                        <div className="col-4" key={`${scenario.id}-${metric.label}`}>
                                            <div className="rounded-3 bg-body-tertiary p-2 h-100">
                                                <div className="small text-secondary mb-1" style={{ fontSize: "12px" }}>{metric.label}</div>
                                                <div className="fw-semibold" style={{ color: "#006a57", fontSize: "13px" }}>{metric.value}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-auto rounded-3 bg-body-tertiary p-3 small text-secondary">
                                    {isValidDropTarget ? "Drop another scenario here to replace this slot." : "Drag a different scenario here to replace this slot."}
                                </div>
                            </div>
                        )
                    : (
                            <div className="d-flex flex-column align-items-center justify-content-center text-center h-100 p-3">
                                <span
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
                                    style={{
                                        width: "80px",
                                        height: "80px",
                                        backgroundColor: isDropActive ? "#bfdbfe" : isValidDropTarget ? "#d1fae5" : "#f8fafc",
                                        color: isDropActive ? "#0d6efd" : isValidDropTarget ? "#059669" : "#94a3b8"
                                    }}
                                >
                                    <SvgIcon icon={plusIcon} style={{ width: "34px", height: "34px" }} />
                                </span>
                                <h2 className="h6 fw-bold mb-3" style={{ color: isValidDropTarget ? "#047857" : "#475569" }}>
                                    Scenario
                                    {" "}
                                    {slotIndex + 1}
                                </h2>
                                <p className="mb-0 small" style={{ color: isValidDropTarget ? "#059669" : "#94a3b8" }}>
                                    {isDropActive ? "Release to add scenario" : "Drag scenario from library"}
                                </p>
                            </div>
                        )}
            </div>
        </Droppable>
    );
};

const ScenarioComparison: React.FC<ScenarioComparisonProps> = ({ selectedScenarios, draggingScenario, onScenarioDrop, onScenarioRemove }) => {
    const selectedCount: number = selectedScenarios.filter(Boolean).length;

    return (
        <section className="h-100 w-100 d-flex flex-column bg-body-tertiary px-3 py-3 overflow-hidden">
            <header className="d-flex flex-column flex-xl-row align-items-xl-start justify-content-between gap-2 mb-4 flex-shrink-0">
                <div>
                    <h1 className="h4 fw-bold text-body mb-2">Scenario Comparison</h1>
                    <p className="mb-0 text-body-secondary" style={{ fontSize: "0.92rem", lineHeight: 1.45 }}>
                        Drag scenarios from the library into the slots below to compare commercial outcomes side-by-side
                    </p>
                </div>
                <span className="text-secondary-emphasis small text-xl-end flex-shrink-0 pt-xl-1">
                    {selectedCount}
                    {" "}
                    of 3 scenarios selected
                </span>
            </header>

            <div className="d-flex flex-row gap-3 flex-grow-1 overflow-auto pb-1">
                {selectedScenarios.map((scenario, index) => (
                    <ScenarioComparisonSlot
                        key={index}
                        scenario={scenario}
                        slotIndex={index}
                        draggingScenario={draggingScenario}
                        onScenarioDrop={onScenarioDrop}
                        onScenarioRemove={onScenarioRemove}
                    />
                ))}
            </div>
        </section>
    );
};

export default ScenarioComparison;
